import React from 'react';
import { Formik, yupToFormErrors } from 'formik';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import moment from 'moment';
import * as Yup from 'yup';
import jstz from 'jstz';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

export default class ProjectForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: undefined,
      loadingUnits: false,
      loadingLeases: false,
      enableSelectingUnit: false,
    };
  }

  componentDidMount() {
    if (this.props.formData.property_id) {
      this.props.fetchUnitsOfProperty(this.props.formData.property_id);
    }
    const timezone = jstz.determine();

    this.setState({
      timezone: timezone.name() || 'America/Toronto',
    });
  }

  handlePropertyChange = async (value, setFieldValue) => {
    setFieldValue('property_id', value);
    setFieldValue('unit_id', undefined);
    setFieldValue('property_option', 'property');
    this.setState({ loadingUnits: true, enableSelectingUnit: false });
    await this.props.fetchUnitsOfProperty(value);
    this.setState({ loadingUnits: false });
  };

  handlePropertyOptionChange = (value, setFieldValue) => {
    this.setState({ enableSelectingUnit: value === 'unit' });
    setFieldValue('property_option', value);
    if (value === 'property') {
      setFieldValue('unit_id', undefined);
    }
  };

  handleUnitChange = async (value, setFieldValue) => {
    this.setState({ loadingLeases: true });
    setFieldValue('unit_id', value);
  };

  handleLoadingChange = (options) => {
    this.props.onLoadingChange && this.props.onLoadingChange(options);
  };

  handleFormSubmit = (values) => {
    const payload = {
      ...values,
      due_at: values.due_at && moment(values.due_at).format('YYYY-MM-DD'),
    };
    this.handleLoadingChange({ isLoading: true, values: payload });
    if (values.id) {
      this.props.putProject(payload).then(() => {
        this.props.onSubmissionComplete(payload);
      });
    } else {
      this.props
        .postProject(payload)
        .done((response) => {
          this.props.onSubmissionComplete(payload, response.project);
        })
        .catch((error) => {
          this.handleLoadingChange({
            isError: true,
            isLoading: false,
            values: payload,
          });
        });
    }
  };

  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');

    let validationSchema = {
      property_id: Yup.string().required(requiredErrorVerbiage),
      property_option: Yup.string().required(requiredErrorVerbiage),
      unit_id: this.state.enableSelectingUnit
        ? Yup.string().required(requiredErrorVerbiage)
        : Yup.string(),
      name: Yup.string()
        .required(requiredErrorVerbiage)
        .max(50, getTranslatedString('landlordPortal.maintenance.projects.max50')),
      description: Yup.string()
        .required(requiredErrorVerbiage)
        .max(250, getTranslatedString('landlordPortal.maintenance.projects.max250')),
      due_at: Yup.date().nullable(),
    };

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  render() {
    const { formData } = this.props;
    return (
      <React.Fragment>
        <Formik
          initialValues={{
            id: formData.id || undefined,
            property_id: formData.property_id,
            property_option: formData.unit_id ? 'unit' : 'property',
            unit_id: formData.unit_id || undefined,
            name: formData.name,
            description: formData.description || undefined,
            due_at: (formData.due_at && moment(formData.due_at)) || undefined,
          }}
          onSubmit={(values) => this.handleFormSubmit(values)}
          validate={this.validateForm}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => (
            <VerticalFormWrapper
              id={this.props.formName}
              onSubmit={(e) => {
                e.stopPropagation();
                handleSubmit(e);
              }}
            >
              {this.renderLocationSection(values, setFieldValue, touched, errors)}
              {this.renderDetailsSection(values, errors, touched, handleChange, setFieldValue)}
            </VerticalFormWrapper>
          )}
        </Formik>
      </React.Fragment>
    );
  }

  renderLocationSection = (values, setFieldValue, touched, errors) => {
    return (
      <React.Fragment>
        <Spacer mb={1}>
          <FormTitle>
            {getTranslatedString('landlordPortal.maintenance.projects.location')}
          </FormTitle>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>{this.renderPropertyField(values, setFieldValue, touched, errors)}</Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              {this.renderPropertyOptionsField(values, setFieldValue, touched, errors)}
            </Col>
          </Row>
        </Spacer>
        {this.state.enableSelectingUnit && (
          <Spacer mb={1}>
            <Row gutter={16}>
              <Col span={24}>{this.renderUnitField(values, setFieldValue, touched, errors)}</Col>
            </Row>
          </Spacer>
        )}
      </React.Fragment>
    );
  };

  renderPropertyField = (values, setFieldValue, touched, errors) => {
    const { properties } = this.props;
    return (
      <Form.Item
        label={`${getTranslatedString('landlordPortal.maintenance.projects.property')}*`}
        validateStatus={touched.property_id && errors.property_id ? 'error' : null}
        help={touched.property_id && errors.property_id}
      >
        <FullWidthSelect
          name="property_id"
          showSearch
          placeholder={getTranslatedString('landlordPortal.maintenance.projects.select')}
          size="large"
          value={values.property_id}
          onChange={(value) => this.handlePropertyChange(value, setFieldValue)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          disabled={this.props.disabledPropertySelect}
        >
          {properties.map((property) => (
            <Select.Option value={property.value} key={property.value}>
              {property.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderPropertyOptionsField = (values, setFieldValue, touched, errors) => {
    return (
      <Form.Item
        label={`${getTranslatedString('landlordPortal.maintenance.projects.propertyOptions')}*`}
        validateStatus={touched.property_option && errors.property_option ? 'error' : null}
        help={touched.property_option && errors.property_option}
      >
        <FullWidthSelect
          name="property_option"
          showSearch
          placeholder="Select"
          size="large"
          value={values.property_option}
          onChange={(value) => this.handlePropertyOptionChange(value, setFieldValue)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          disabled={!values.property_id || this.props.disabledPropertySelect}
        >
          <Select.Option key="property_option_1" value="property">
            {getTranslatedString('landlordPortal.maintenance.projects.assignToProperty')}
          </Select.Option>
          <Select.Option key="property_option_2" value="unit">
            {getTranslatedString('landlordPortal.maintenance.projects.unitsWithinProperty')}
          </Select.Option>
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderUnitField = (values, setFieldValue, touched, errors) => {
    const units = this.props.units || [];
    const { loadingUnits } = this.state;
    return (
      <Form.Item
        label={`${getTranslatedString('landlordPortal.maintenance.projects.unit')}*`}
        validateStatus={touched.unit_id && errors.unit_id ? 'error' : null}
        help={touched.unit_id && errors.unit_id}
      >
        <FullWidthSelect
          name="unit_id"
          showSearch
          placeholder="Select"
          size="large"
          value={values.unit_id}
          disabled={loadingUnits || units.length === 0 || this.props.disabledPropertySelect}
          loading={loadingUnits}
          onChange={(value) => this.handleUnitChange(value, setFieldValue)}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {units.map((unit) => (
            <Select.Option key={unit} value={unit.value}>
              {unit.label}
            </Select.Option>
          ))}
        </FullWidthSelect>
      </Form.Item>
    );
  };

  renderDetailsSection = (values, errors, touched, handleChange, setFieldValue) => {
    return (
      <React.Fragment>
        <Spacer mt={2} mb={1}>
          <FormTitle>
            {getTranslatedString('landlordPortal.maintenance.projects.projectDetails')}
          </FormTitle>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={`${getTranslatedString('landlordPortal.maintenance.projects.projectName')}*`}
                validateStatus={touched.name && errors.name ? 'error' : null}
                help={touched.name && errors.name}
              >
                <FullWidthInput
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.projects.namePlaceholder'
                  )}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={`${getTranslatedString('landlordPortal.maintenance.projects.description')}*`}
                validateStatus={touched.description && errors.description ? 'error' : null}
                help={touched.description && errors.description}
              >
                <FullWidthTextArea
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  placeholder={getTranslatedString(
                    'landlordPortal.maintenance.projects.descriptionPlaceholder'
                  )}
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
        <Spacer mb={1}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={getTranslatedString('landlordPortal.maintenance.projects.dueDate')}
                validateStatus={touched.due_at && errors.due_at ? 'error' : null}
                help={touched.due_at && errors.due_at}
              >
                <FullWidthDatePicker
                  name="due_at"
                  defaultPickerValue={moment().locale(getCurrentLocale())}
                  showToday={false}
                  value={values.due_at}
                  onChange={(value) => setFieldValue('due_at', value, true)}
                  placeholder="e.g. 2020-01-01"
                  mobileMode="date"
                  size="large"
                  id="error"
                />
              </Form.Item>
            </Col>
          </Row>
        </Spacer>
      </React.Fragment>
    );
  };
}
